import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Pause, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MEDIA_STICKERS_QUERY_KEY } from '../api';
import {
  mapPayloadFiles,
  missingPayloadFiles,
  parseStickerImportManifest,
  releaseManifest,
  validatePayloadFile,
  type StickerImportManifest,
} from '../stickerImport';
import {
  stickerReleases,
  type StickerPublication,
  type StickerReleaseStatus,
} from '../stickerReleases';

export function StickerReleaseDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [manifest, setManifest] = useState<StickerImportManifest | null>(null);
  const [files, setFiles] = useState(new Map<string, File>());
  const [releases, setReleases] = useState<StickerReleaseStatus[]>([]);
  const [publication, setPublication] = useState<StickerPublication | null>(null);
  const [job, setJob] = useState<StickerReleaseStatus | null>(null);
  const [releaseId, setReleaseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activity, setActivity] = useState('');
  const folderRef = useRef<HTMLInputElement>(null);
  const pauseRef = useRef(false);
  const missingFiles = manifest ? missingPayloadFiles(manifest, files) : [];

  useEffect(() => {
    folderRef.current?.setAttribute('webkitdirectory', '');
  }, [open]);

  const refresh = async (id?: string) => {
    const [currentReleases, publications] = await Promise.all([
      stickerReleases.list(),
      stickerReleases.publications(),
    ]);
    setReleases(currentReleases);
    setPublication(publications[0] ?? null);
    if (id) setJob(await stickerReleases.status(id));
  };

  useEffect(() => {
    if (!open) return;
    void refresh().catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : 'Could not load import jobs'),
    );
  }, [open]);

  useEffect(() => {
    if (!open || !job || job.status !== 'PUBLISHING') return;
    const timer = window.setInterval(() => {
      void refresh(job.releaseId).catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Could not refresh publication'),
      );
    }, 5000);
    return () => window.clearInterval(timer);
  }, [open, job]);

  useEffect(() => {
    if (open && (job?.status === 'PUBLISHED' || job?.status === 'RETIRED')) {
      void qc.invalidateQueries({ queryKey: MEDIA_STICKERS_QUERY_KEY });
    }
  }, [open, job?.status, job?.releaseId, qc]);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await operation();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Operation failed');
    } finally {
      setBusy(false);
      setActivity('');
    }
  };

  const readManifest = async (file?: File) => {
    if (!file) return;
    await run(async () => {
      const parsed = parseStickerImportManifest(JSON.parse(await file.text()) as unknown);
      setManifest(parsed);
      setReleaseId(parsed.releaseId);
      setJob(null);
    });
  };

  const upload = async () => {
    if (!manifest || missingFiles.length) return;
    await run(async () => {
      pauseRef.current = false;
      setReleaseId(manifest.releaseId);
      let current = await stickerReleases.create(releaseManifest(manifest));
      setJob(current);
      if (['PUBLISHED', 'RETIRED', 'PUBLISHING'].includes(current.status)) return;
      const assets = new Map(manifest.stickers.map((asset) => [asset.id, asset]));
      for (const missing of current.missingAssets) {
        const asset = assets.get(missing.stickerId);
        if (!asset) throw new Error(`Server returned an unexpected sticker: ${missing.stickerId}`);
        for (const kind of ['source', 'thumbnail'] as const) {
          if (pauseRef.current) break;
          if (
            (kind === 'source' && missing.sourceUploaded) ||
            (kind === 'thumbnail' && missing.thumbnailUploaded)
          )
            continue;
          const path = kind === 'source' ? asset.sourcePath : asset.thumbnailPath;
          const file = files.get(path);
          if (!file) throw new Error(`Missing local file: ${path}`);
          setActivity(`Uploading ${asset.name} — ${kind}`);
          await validatePayloadFile(
            file,
            kind === 'source' ? asset.bytes : asset.thumbnailBytes,
            kind === 'source' ? asset.sha256 : asset.thumbnailSha256,
          );
          await stickerReleases.upload(manifest.releaseId, asset.id, kind, file);
          setJob((previous) =>
            previous
              ? {
                  ...previous,
                  sourceUploadedCount: previous.sourceUploadedCount + Number(kind === 'source'),
                  thumbnailUploadedCount:
                    previous.thumbnailUploadedCount + Number(kind === 'thumbnail'),
                }
              : previous,
          );
        }
        if (pauseRef.current) break;
      }
      current = await stickerReleases.status(manifest.releaseId);
      setJob(current);
      await refresh(manifest.releaseId);
    });
  };

  const action = async (name: 'validate' | 'activate' | 'retry' | 'rollback') => {
    if (!job) return;
    await run(async () => {
      pauseRef.current = false;
      setActivity(name === 'activate' ? 'Publishing the complete library…' : `${name}…`);
      let next = await stickerReleases.action(job.releaseId, name);
      setJob(next);
      while (name === 'validate' && next.status === 'VALIDATING' && !pauseRef.current) {
        setActivity(
          `Validating images: ${next.verifiedAssetCount ?? 0}/${next.expectedAssetCount ?? next.expectedCount * 2}`,
        );
        next = await stickerReleases.action(job.releaseId, 'validate');
        setJob(next);
      }
      await refresh(job.releaseId);
    });
  };

  const uploaded = (job?.sourceUploadedCount ?? 0) + (job?.thumbnailUploadedCount ?? 0);
  const expected = (job?.expectedCount ?? manifest?.stickers.length ?? 0) * 2;
  const complete =
    !!job &&
    job.sourceUploadedCount === job.expectedCount &&
    job.thumbnailUploadedCount === job.expectedCount;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Replace sticker library</DialogTitle>
          <DialogDescription>
            Upload a complete grouped library, validate it, then publish once. The previous artwork
            remains in the archive for existing drafts.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-sm font-medium">
              Import manifest
              <Input
                type="file"
                accept="application/json,.json"
                disabled={busy}
                onChange={(e) => {
                  void readManifest(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            <label className="space-y-1 text-sm font-medium">
              Payload folder
              <Input
                ref={folderRef}
                type="file"
                multiple
                disabled={busy}
                onChange={(e) => setFiles(mapPayloadFiles(Array.from(e.target.files ?? [])))}
              />
            </label>
          </div>
          {manifest && (
            <div className="rounded-md border p-3 text-sm">
              <p className="font-medium">
                {manifest.stickers.length} stickers · {manifest.categories.length} groups ·{' '}
                {manifest.version}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {[...manifest.categories]
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((category) => (
                    <span key={category.id} className="rounded bg-muted px-2 py-1">
                      {category.displayName}:{' '}
                      {manifest.stickers.filter((asset) => asset.categoryId === category.id).length}
                    </span>
                  ))}
              </div>
              <p className="mt-2 text-muted-foreground">
                {missingFiles.length
                  ? `${missingFiles.length} required files are missing. Select the payload folder.`
                  : 'All source images and thumbnails are present.'}
              </p>
              {missingFiles.length > 0 && (
                <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                  {missingFiles.slice(0, 3).join(', ')}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-56 flex-1 space-y-1 text-sm">
              Resume an import
              <select
                aria-label="Resume an import"
                value={releaseId}
                disabled={busy}
                onChange={(e) => {
                  setReleaseId(e.target.value);
                  setJob(null);
                }}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              >
                <option value="">Select an import</option>
                {manifest && !releases.some((item) => item.releaseId === manifest.releaseId) && (
                  <option value={manifest.releaseId}>{manifest.releaseId} — local manifest</option>
                )}
                {releases.map((item) => (
                  <option key={item.releaseId} value={item.releaseId}>
                    {item.releaseId} — {item.status}
                  </option>
                ))}
              </select>
            </label>
            <Button
              variant="outline"
              disabled={busy || !releaseId}
              onClick={() => void run(() => refresh(releaseId))}
            >
              <RefreshCw className="size-4" />
              Refresh status
            </Button>
          </div>
          {job && (
            <div className="space-y-2 rounded-md border p-3 text-sm" aria-live="polite">
              <p className="font-medium">
                {job.releaseId} · {job.status}
              </p>
              <progress
                className="h-2 w-full"
                max={expected || 1}
                value={uploaded}
                aria-label="Upload progress"
              />
              <p>
                Sources: {job.sourceUploadedCount}/{job.expectedCount} · Thumbnails:{' '}
                {job.thumbnailUploadedCount}/{job.expectedCount}
              </p>
              {job.status === 'VALIDATING' && (
                <p>
                  Verified images: {job.verifiedAssetCount ?? 0}/
                  {job.expectedAssetCount ?? job.expectedCount * 2}
                </p>
              )}
              {job.publicationId && (
                <p className="break-all font-mono text-xs">Publication: {job.publicationId}</p>
              )}
              {job.error && <p className="text-destructive">{job.error}</p>}
              {job.status === 'READY' && (
                <p>Ready to replace every currently listed sticker with this complete library.</p>
              )}
              {job.status === 'PUBLISHED' && (
                <p className="text-green-700">
                  The complete catalog has been published. Retired items are available in Archive.
                </p>
              )}
              {job.status === 'RETIRED' && (
                <p>This release is archived. The current library is available on the main page.</p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={
                    busy || !complete || ['PUBLISHED', 'PUBLISHING', 'RETIRED'].includes(job.status)
                  }
                  onClick={() => void action('validate')}
                >
                  Validate library
                </Button>
                <Button
                  disabled={busy || job.status !== 'READY'}
                  onClick={() => void action('activate')}
                >
                  Publish replacement
                </Button>
                {job.status === 'FAILED' && job.publicationId && (
                  <Button variant="outline" disabled={busy} onClick={() => void action('retry')}>
                    Retry publication
                  </Button>
                )}
                {job.status === 'PUBLISHED' && (
                  <Button variant="outline" disabled={busy} onClick={() => void action('rollback')}>
                    Restore previous library
                  </Button>
                )}
              </div>
            </div>
          )}
          {publication && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Latest publication: {publication.operation} · {publication.status} ·{' '}
                {publication.version}
                {publication.error ? ` · ${publication.error}` : ''}
              </p>
              {publication.status === 'FAILED' && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await stickerReleases.retryPublication(publication.publicationId);
                      await refresh(job?.releaseId);
                      await qc.invalidateQueries({ queryKey: MEDIA_STICKERS_QUERY_KEY });
                    })
                  }
                >
                  Retry latest publication
                </Button>
              )}
            </div>
          )}
          {activity && (
            <p className="flex items-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" />
              {activity}
            </p>
          )}
          {error && (
            <p
              role="alert"
              className="break-words rounded-md border border-destructive/30 p-3 text-sm text-destructive"
            >
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          {busy && /^(Uploading|Validating|validate)/.test(activity) && (
            <Button
              variant="outline"
              onClick={() => {
                pauseRef.current = true;
              }}
            >
              <Pause className="size-4" />
              Pause after this file / validation batch
            </Button>
          )}
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Close
          </Button>
          <Button
            disabled={
              busy ||
              !manifest ||
              missingFiles.length > 0 ||
              (job?.releaseId === manifest.releaseId &&
                ['PUBLISHED', 'PUBLISHING', 'RETIRED'].includes(job.status))
            }
            onClick={() => void upload()}
          >
            Upload / resume files
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
